@if (isEmployeeDetailsLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  <div class="animate-fade-in">
    <header class="page-header mb-4">
      <div class="header-left">
        <button class="back-btn" (click)="onBackToEmployees()" title="Back to Employees">
          <i class="bi bi-arrow-left"></i>
        </button>
        <div class="header-titles">
          <h1 class="product-name">Employee Details</h1>
          <span class="product-category">
            {{ employee?.user?.first_name + ' ' + employee?.user?.last_name }}
          </span>
        </div>
      </div>

      <div class="header-right d-flex align-items-center gap-2">
        <button
          class="btn btn-soft-primary d-flex align-items-center gap-2"
          (click)="onExportProfile()"
          [disabled]="isExportingProfile">
          @if (isExportingProfile) {
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Exporting...
          } @else {
            <i class="bi bi-download"></i>
            Export Employee Profile
          }
        </button>
        <button class="btn btn-primary" (click)="openResetPasswordModal()">
          <i class="bi bi-key-fill"></i>
          <span>Reset Password</span>
        </button>
      </div>
    </header>

    <div class="row">
      <div class="col-12 px-0 mb-4">
        <div class="card p-0 border-0">
          <div class="card-body px-1">
            <div class="row align-items-center">
              <!-- Profile Info -->
              <div class="col-12 col-md-5 mb-4 mb-md-0">
                <div class="d-flex align-items-center">
                  <img
                    [src]="
                      employee?.user?.profile_picture ||
                      getDefaultProfilePicUrl(employee?.user?.first_name || '', employee?.user?.last_name || '')
                    "
                    class="img-thumbnail me-4"
                    width="100"
                    height="100"
                    alt="{{ employee?.user?.first_name }} {{ employee?.user?.last_name }}"
                    (error)="handleImageError($event)" />

                  <div>
                    <h4 class="mb-1 fw-semibold">
                      {{ employee?.user?.first_name + ' ' + employee?.user?.last_name }}
                    </h4>
                    <p class="mb-1 text-muted">{{ employee?.designation?.title ?? '-' }}</p>
                    <span class="app-badge badge-success">Active</span>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-7">
                <div class="row g-3">
                  <!-- Info Card Template -->
                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle blue-icon-bg bg-opacity-10 blue-icon">
                        <i class="bi bi-envelope"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Email</div>
                        <div class="fw-semibold text-break">{{ employee?.user?.email ?? '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle purple-icon-bg bg-opacity-10 purple-icon">
                        <i class="bi bi-geo-alt"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Location</div>
                        <div class="fw-semibold text-break">{{ employee?.user?.city ?? '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle green-icon-bg bg-opacity-10 green-icon">
                        <i class="bi bi-telephone"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Phone</div>
                        <div class="fw-semibold text-break">{{ employee?.user?.phone_number ?? '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle orange-icon-bg bg-opacity-10 orange-icon">
                        <i class="bi bi-calendar4"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Join Date</div>
                        <div class="fw-semibold text-break">{{ employee?.date_of_joining ?? '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle purple-icon-bg bg-opacity-10 purple-icon">
                        <i class="bi bi-person-badge"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Aadhar Card</div>
                        <div class="fw-semibold text-break">{{ employee?.user?.aadhar_card_number ?? '-' }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="info-card d-flex align-items-center p-2 border gap-3">
                      <div class="icon-circle green-icon-bg bg-opacity-10 green-icon">
                        <i class="bi bi-cake2"></i>
                      </div>
                      <div>
                        <div class="text-muted small">Date of Birth</div>
                        <div class="fw-semibold text-break">{{ employee?.user?.date_of_birth ?? '-' }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4 px-0">
      <div class="col-12 px-0">
        <div class="card border-0 p-0">
          <div class="card-header bg-white border-0 d-flex align-items-center gap-2 px-3 pt-3 pb-2">
            <i class="bi bi-file-earmark-spreadsheet text-primary fs-5"></i>
            <span class="fw-semibold">Reports</span>
          </div>
          <div class="card-body p-3 pt-1">
            <!-- Monthly Attendance Sheet -->
            <div class="d-flex align-items-center gap-3 py-3 border-bottom flex-wrap">
              <div class="d-flex align-items-center gap-2" style="min-width: 200px">
                <i class="bi bi-calendar2-month text-info"></i>
                <span class="fw-semibold small">Monthly Attendance Sheet</span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                <div>
                  <label class="form-label small text-muted mb-1">Year</label>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    style="width: 100px"
                    [(ngModel)]="monthlyAttendanceYear"
                    [min]="2020"
                    [max]="2100" />
                </div>
                <div>
                  <label class="form-label small text-muted mb-1">Month</label>
                  <ng-select
                    placeholder="Select month"
                    [items]="monthOptions"
                    [(ngModel)]="monthlyAttendanceMonth"
                    bindValue="value"
                    bindLabel="label"
                    [clearable]="false"
                    [searchable]="false"
                    style="min-width: 140px" />
                </div>
              </div>
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 ms-auto"
                (click)="onExportMonthlyAttendance()"
                [disabled]="isExportingMonthlyAttendance">
                @if (isExportingMonthlyAttendance) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Download
                }
              </button>
            </div>

            <!-- Yearly Attendance Sheet -->
            <div class="d-flex align-items-center gap-3 py-3 border-bottom flex-wrap">
              <div class="d-flex align-items-center gap-2" style="min-width: 200px">
                <i class="bi bi-calendar-check text-primary"></i>
                <span class="fw-semibold small">Yearly Attendance Sheet</span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                <div>
                  <label class="form-label small text-muted mb-1">Year</label>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    style="width: 100px"
                    [(ngModel)]="exportAttendanceYear"
                    [min]="2020"
                    [max]="2100" />
                </div>
              </div>
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 ms-auto"
                (click)="onExportYearlyAttendance()"
                [disabled]="isExportingYearlyAttendance">
                @if (isExportingYearlyAttendance) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Download
                }
              </button>
            </div>

            <!-- Route & Time Report -->
            <div class="d-flex align-items-center gap-3 py-3 border-bottom flex-wrap">
              <div class="d-flex align-items-center gap-2" style="min-width: 200px">
                <i class="bi bi-geo-alt text-success"></i>
                <span class="fw-semibold small">Route & Time Report</span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                <div>
                  <label class="form-label small text-muted mb-1">Year</label>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    style="width: 100px"
                    [(ngModel)]="routeReportYear"
                    [min]="2020"
                    [max]="2100" />
                </div>
                <div>
                  <label class="form-label small text-muted mb-1">Month</label>
                  <ng-select
                    placeholder="All months"
                    [items]="monthOptions"
                    [(ngModel)]="routeReportMonth"
                    bindValue="value"
                    bindLabel="label"
                    [clearable]="true"
                    [searchable]="false"
                    style="min-width: 140px" />
                </div>
              </div>
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 ms-auto"
                (click)="onExportRouteReport()"
                [disabled]="isExportingRouteReport">
                @if (isExportingRouteReport) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Download
                }
              </button>
            </div>

            <!-- Travel Allowance Report -->
            <div class="d-flex align-items-center gap-3 py-3 flex-wrap">
              <div class="d-flex align-items-center gap-2" style="min-width: 200px">
                <i class="bi bi-car-front text-warning"></i>
                <span class="fw-semibold small">Travel Allowance Report</span>
              </div>
              <div class="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
                <div>
                  <label class="form-label small text-muted mb-1">Year</label>
                  <input
                    type="number"
                    class="form-control form-control-sm"
                    style="width: 100px"
                    [(ngModel)]="taReportYear"
                    [min]="2020"
                    [max]="2100" />
                </div>
                <div>
                  <label class="form-label small text-muted mb-1">Month</label>
                  <ng-select
                    placeholder="All months"
                    [items]="monthOptions"
                    [(ngModel)]="taReportMonth"
                    bindValue="value"
                    bindLabel="label"
                    [clearable]="true"
                    [searchable]="false"
                    style="min-width: 140px" />
                </div>
              </div>
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 ms-auto"
                (click)="onExportTaReport()"
                [disabled]="isExportingTaReport">
                @if (isExportingTaReport) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Download
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (isInSalesTeamEmployee(employee!)) {
      <div class="col-12 px-0 mb-4">
        <div class="card border-0 p-3">
          <div class="card-header bg-white border-0 d-flex align-items-center gap-2">
            <span class="fw-semibold">Location Details</span>
          </div>
          <div class="card-body p-0">
            <div class="g-3">
              <div class="col-12">
                <div class="d-flex flex-row justify-content-between gap-3 p-3 shadow-sm">
                  <div class="d-flex align-items-center gap-2">
                    <i class="bi bi-geo-alt purple-icon fs-5"></i>

                    <div>
                      <div class="text-muted small">Current Location</div>

                      <div class="fw-semibold">
                        {{ empLocation?.address || 'Location not available' }}
                      </div>

                      <!-- Last update time -->
                      <div class="text-muted small mt-1 d-flex align-items-center gap-1">
                        Last updated:
                        {{ lastUpdatedLocationTime | date: 'dd MMM yyyy, hh:mm a' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12">
                <div class="shadow-sm text-center" *ngIf="empLocation; else wsErrorBlock">
                  <google-map height="400px" width="100%" [center]="center" [zoom]="zoom">
                    <map-marker
                      *ngFor="let pos of markerPositions"
                      [position]="pos"
                      [title]="'Employee Current Location'"></map-marker>
                  </google-map>
                </div>

                <ng-template #wsErrorBlock>
                  <div class="p-4 bg-light shadow-sm text-center text-danger">
                    {{ wsError || 'Waiting for live location update...' }}
                  </div>
                </ng-template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-4 px-3">
        <div class="col-12 col-md-6 px-0 pe-md-2">
          <div class="card p-0 border-1">
            <div
              class="card-header bg-white border-0 d-flex flex-row justify-content-between align-items-center flex-wrap">
              <div class="d-flex align-items-center gap-2">
                <i class="bi bi-shop fs-2 text-primary"></i>
                <span class="fw-semibold">Dealers</span>
              </div>

              <!--
              <div>
                <button class="btn btn-primary btn-enjochem" (click)="openAssignDealerModal()">
                  <i class="bi bi-person-plus-fill"></i>
                  <span>Assign New Dealer</span>
                </button>
              </div>
              -->
            </div>

            <div class="card-body px-0">
              @if (empDealers.length === 0) {
                <div class="text-center text-muted py-3">
                  <p class="fs-5">No dealers assigned yet.</p>
                </div>
              } @else {
                <ul class="list-group list-group-flush">
                  <li
                    class="list-group-item d-flex justify-content-between align-items-center"
                    *ngFor="let dealer of empDealers">
                    <div>
                      <span>
                        <div>
                          <span>
                            <i class="bi bi-house-door me-1 text-primary"></i>
                            {{ dealer.shop_name }}
                          </span>
                        </div>
                        <span class="text-muted">
                          {{ dealer.first_name + ' ' + dealer.last_name + ' - ' + dealer.phone_number }}
                        </span>
                      </span>
                    </div>
                    <button class="btn btn-sm btn-outline-danger rounded-0" (click)="onUnassignDealer(dealer.id)">
                      <i class="bi bi-trash3"></i>
                      Remove
                    </button>
                  </li>
                </ul>
              }
            </div>
          </div>
        </div>

        <div class="col-12 col-md-6 px-0 ps-md-2">
          <div class="card p-0 border-1">
            <div
              class="card-header bg-white border-0 d-flex flex-row justify-content-between align-items-center flex-wrap">
              <div class="d-flex align-items-center gap-2">
                <i class="bi bi-bag-check fs-2 text-success"></i>
                <span class="fw-semibold">Products Promoted</span>
              </div>

              @if (employee?.products_promoted?.length) {
                <span class="app-badge badge-secondary">{{ employee?.products_promoted?.length }}</span>
              }
            </div>

            <div class="card-body px-0">
              @if (employee?.products_promoted?.length === 0) {
                <div class="text-center text-muted py-3">
                  <p class="fs-5">No products promoted yet.</p>
                </div>
              } @else {
                <ul class="list-group list-group-flush">
                  <li *ngFor="let product of employee?.products_promoted" class="list-group-item">
                    <div class="d-flex align-items-center">
                      <!-- Product Image -->
                      <img
                        *ngIf="false; else noImage"
                        [src]="getDefaultProfilePicUrl(product.name)"
                        alt="{{ product.name }}"
                        class="me-3"
                        style="width: 50px; height: 50px; object-fit: cover" />
                      <ng-template #noImage>
                        <div
                          class="bg-light border me-3 d-flex justify-content-center align-items-center"
                          style="width: 50px; height: 50px">
                          <span class="text-muted small">No Img</span>
                        </div>
                      </ng-template>

                      <!-- Product Info -->
                      <div>
                        <h6 class="mb-1">{{ product.name }}</h6>
                        <p class="mb-0 text-muted small">{{ product.product_category?.name }}</p>
                        <p class="mb-0 text-muted small">{{ product.contents }}</p>
                        <p class="mb-0 text-muted small">
                          <!--                              {{ product.packing_size }} {{ product.unit }} • ₹{{ product.dealer_rate }}-->
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 px-3 mb-4">
        <div class="card border-1 p-0">
          <div
            class="card-header bg-white border-0 d-flex flex-row justify-content-between align-items-center flex-wrap">
            <div class="d-flex align-items-center gap-2">
              <i class="bi bi-people fs-2 text-success"></i>
              <span class="fw-semibold">Associated Farmers</span>
            </div>

            @if (employee?.farmers?.length) {
              <span class="app-badge badge-secondary">{{ employee?.farmers?.length }}</span>
            }
          </div>

          <div class="card-body px-0">
            @if (employee?.farmers?.length === 0) {
              <div class="text-center text-muted py-3">
                <p class="fs-5">No farmers associated yet.</p>
              </div>
            } @else {
              <div class="row g-3">
                <div class="col-12 col-md-6" *ngFor="let farmer of employee?.farmers">
                  <div class="border p-3 h-100">
                    <h6 class="mb-1">{{ farmer.full_name | titlecase }}</h6>
                    <p class="mb-1 text-muted small">
                      <i class="bi bi-geo-alt"></i>
                      {{ farmer.vilage }}, {{ farmer.taluka }}, {{ farmer.district }}, {{ farmer.state }}
                    </p>
                    <p class="mb-1 text-muted small">
                      <i class="bi bi-telephone"></i>
                      {{ farmer.mobile_number }}
                      <span *ngIf="farmer.whatsapp_number">• WhatsApp: {{ farmer.whatsapp_number }}</span>
                    </p>
                    <p class="mb-1 text-muted small">
                      <strong>Crops:</strong>
                      {{ (farmer.crops_grown || []).join(', ') || 'N/A' }}
                    </p>
                    <p class="mb-0 text-muted small">
                      <strong>Land Size:</strong>
                      {{ +farmer.land_size | number: '1.0-0' }} Acre
                    </p>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>
}

<ng-template #assignDealerContent>
  <div class="modal-header">
    <h5 class="modal-title">Assign Dealers</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="modalRef?.close()"></button>
  </div>
  <div class="modal-body" *ngIf="dealers.length > 0; else loadingOrEmpty">
    <div *ngFor="let dealer of dealers">
      <div class="form-check">
        <input
          class="form-check-input"
          type="checkbox"
          [id]="dealer.id"
          [value]="dealer.id"
          [(ngModel)]="dealer.selected" />
        <label class="form-check-label" [for]="dealer.id">
          {{ dealer.shop_name }} ({{ dealer.first_name }} {{ dealer.last_name }})
        </label>
      </div>
    </div>
  </div>

  <ng-template #loadingOrEmpty>
    <div class="d-flex flex-column justify-content-center align-items-center py-5 w-100">
      <p *ngIf="isDealerDataLoading" class="mb-0 text-muted">Loading dealers...</p>
      <div *ngIf="!isDealerDataLoading && dealers.length === 0" class="text-center text-muted">
        <i class="bi bi-shop fs-1 mb-2"></i>
        <p class="mb-0">No dealers found.</p>
      </div>
    </div>
  </ng-template>

  <div class="modal-footer">
    <button class="btn btn-secondary d-flex align-items-center gap-2" (click)="modalRef?.close()">
      <i class="bi bi-arrow-left-circle"></i>
      <span>Cancel</span>
    </button>
    <button class="btn btn-primary btn-enjochem d-flex align-items-center gap-2" (click)="assignSelectedDealers()">
      <ng-container *ngIf="isMappingDealers; else saveIcon">
        <app-loading-spinner [inline]="true" class="me-1"></app-loading-spinner>
      </ng-container>

      <ng-template #saveIcon>
        <i class="bi bi-check-circle"></i>
      </ng-template>
      <span>Assign</span>
    </button>
  </div>
</ng-template>

<ng-template #resetPasswordContent>
  <div class="modal-header">
    <h5 class="modal-title">Reset Password</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="modalRef?.close()"></button>
  </div>
  <div class="modal-body">
    <div class="mb-3">
      <label for="newPassword" class="form-label">New Password</label>
      <div class="password-field">
        <input
          [type]="showNewPassword ? 'text' : 'password'"
          class="form-control"
          id="newPassword"
          [(ngModel)]="newPassword"
          placeholder="Enter new password" />
        <button
          type="button"
          class="password-toggle-btn"
          [attr.aria-label]="showNewPassword ? 'Hide new password' : 'Show new password'"
          [attr.aria-pressed]="showNewPassword"
          (click)="showNewPassword = !showNewPassword">
          <i class="bi" [ngClass]="showNewPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
        </button>
      </div>
    </div>
    <div *ngIf="resetPasswordError" class="alert alert-danger mt-3">
      {{ resetPasswordError }}
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-secondary d-flex align-items-center gap-2" (click)="modalRef?.close()">
      <i class="bi bi-x-circle"></i>
      <span>Cancel</span>
    </button>
    <button
      class="btn btn-primary btn-enjochem d-flex align-items-center gap-2"
      (click)="resetPassword()"
      [disabled]="isResettingPassword || !newPassword">
      <ng-container *ngIf="isResettingPassword; else saveIcon">
        <app-loading-spinner [inline]="true" class="me-1"></app-loading-spinner>
      </ng-container>
      <ng-template #saveIcon>
        <i class="bi bi-check-circle"></i>
      </ng-template>
      <span>Save Password</span>
    </button>
  </div>
</ng-template>
